import * as React from 'react';

import {
  ProfileOccupationChoices_DemandLevel as DemandLevel,
  ProfileOccupationChoices_FeatureTag as FeatureTag,
} from '__generated__/graphql-types';

import { useProfileOccupationChoices } from 'bundles/employability-common/queries/hooks/useProfileOccupationChoices';
import { convertProfileOccupationChoiceToPreferenceNaptimeType } from 'bundles/employability-common/queries/utils/convertProfileOccupationChoiceToPreferenceNaptimeType';
import type {
  AvailableOccupation,
  CurrentRoleValue,
} from 'bundles/personalization-settings/hooks/useCurrentRoleEditor';

// Same occupation-tag filter onboarding-common's JobTitleQuestion uses, so
// the catalog offered here matches onboarding.
const OCCUPATION_CHOICE_FILTER = {
  occupationTags: [
    { demandLevel: DemandLevel.Low, featureTag: FeatureTag.BaselineV2 },
    { demandLevel: DemandLevel.High, featureTag: FeatureTag.BaselineV2 },
  ],
};

export type UseResolvedCurrentRoleResult = {
  /** The learner's saved current role, resolved to a display name via the occupation catalog. */
  value: CurrentRoleValue;
  availableOccupations: readonly AvailableOccupation[];
  loading: boolean;
};

/**
 * Resolves the page-level query's raw saved current-role value (`{ id }` for
 * a catalog occupation, `{ name }` for free text) into a display-ready
 * `CurrentRoleValue`. DEFINED occupations only carry an id until resolved
 * against the catalog. Shared by `CurrentRoleSettingItem` (its own editor)
 * and the add-current-role-to-desired-roles prompt (`LearnerObjectSettingItems`),
 * so both read the same resolution instead of duplicating the catalog fetch inline.
 */
const useResolvedCurrentRole = (savedValue: Record<string, string> | null): UseResolvedCurrentRoleResult => {
  const { occupations: rawOccupations, isProfileOccupationChoicesLoading: loading } = useProfileOccupationChoices({
    occupationChoiceFilter: OCCUPATION_CHOICE_FILTER,
  });

  const availableOccupations = React.useMemo(
    () => convertProfileOccupationChoiceToPreferenceNaptimeType(rawOccupations),
    [rawOccupations]
  );

  const value = React.useMemo<CurrentRoleValue>(() => {
    if (!savedValue) return null;
    // savedValue is Record<string,string>: { id } for DEFINED, { name } for USER_GENERATED
    const occupationId = savedValue.id;
    const occupationName = savedValue.name;
    if (occupationId) {
      const match = availableOccupations.find((o) => o.id === occupationId);
      return match ? { id: match.id, name: match.name } : null;
    }
    return occupationName ? { name: occupationName } : null;
  }, [savedValue, availableOccupations]);

  return { value, availableOccupations, loading };
};

export default useResolvedCurrentRole;
